import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSearchParams, useStandardApiRequest } from 'my-core/hooks';
import { findAncestor } from 'my-core/online-course-content-map';

import PracticeModeSection from './PracticeModeSection';

import { fetchOnlineCoursePracticeModeQuestion } from 'my-actions/OnlineCourseActions';
import {
  resetQuizQuestionSubmission,
  savePracticeModeQuizQuestionSubmission,
} from 'my-actions/QuizQuestionSubmissionActions';

function PracticeModeSectionCtnr(props) {
  const {
    activeSection,
    canView,
    courseSubscription,
    currentUser,
    fetchingSubmissions,
    learnModePath,
    nextAndPrev,
    onlineCourse,
    onlineCourseUser,
    onPurchase,
    // onStartTrial,
    onSetActiveQuestionId,
    onTokenPurchase,
    sectionId,
    sectionSubmissions,
    subscription,
    // subscriptionTrialStatus,
    tokenPurchases,
    tokenPurchaseStatus,
    totalQuestions,
    userCourse,
  } = props;
  const [{ qid }, setSearchParams] = useSearchParams();
  const questionIdParam = qid && Number(qid);

  const remainingQuestions = totalQuestions - sectionSubmissions.length;

  const { performRequest: performFetchNewQuestion, requestStatus: fetchNewQuestionStatus } = useStandardApiRequest({
    actionCreator: fetchOnlineCoursePracticeModeQuestion,
    errorMessage: true,
  });

  // cache of fetched unanswered questions for this section
  const [fetchedQuestionIds, setFetchedQuestionIds] = useState([]);

  // currently cacheed unanswered question (for once no more new questions to fetch)
  const [newQuestionId, setNewQuestionId] = useState();

  // update the active question to either fetch new, or loop through the cached questions

  const activeQuestionId =
    questionIdParam ||
    newQuestionId ||
    fetchedQuestionIds[fetchedQuestionIds.length - 1] ||
    (!sectionSubmissions.some(qqs => qqs.quiz_question_id === fetchNewQuestionStatus?.id) &&
      fetchNewQuestionStatus?.id);

  const fetchNewQuestion = useCallback(
    qqId => {
      if (fetchedQuestionIds.length >= remainingQuestions) {
        setNewQuestionId(qid => fetchedQuestionIds[(fetchedQuestionIds.indexOf(qid) + 1) % fetchedQuestionIds.length]);
      } else {
        performFetchNewQuestion(onlineCourse.id, {
          section_id: sectionId,
          ...(qqId ? { quiz_question_id: qqId } : { exclude_ids: fetchedQuestionIds }),
        });
      }
    },
    [onlineCourse.id, sectionId, performFetchNewQuestion, fetchedQuestionIds, remainingQuestions],
  );

  const activeQuestion = useSelector(state =>
    getQuestion(
      activeQuestionId,
      state.quizQuestions.items,
      state.users.items,
      onlineCourse,
      activeSection,
      learnModePath,
    ),
  );

  // auto fetch question on page load (if necessary)
  useEffect(() => {
    if (
      !fetchNewQuestionStatus?.fetching &&
      !fetchNewQuestionStatus?.error &&
      remainingQuestions > 0 &&
      !fetchingSubmissions &&
      activeQuestion?.content === undefined // TODO: better checking of whether loaded or not
    )
      fetchNewQuestion(questionIdParam);
  }, [
    activeQuestion,
    fetchingSubmissions,
    remainingQuestions,
    fetchNewQuestion,
    fetchNewQuestionStatus,
    questionIdParam,
  ]);

  const activeSubmission = useMemo(
    () => sectionSubmissions.find(qqs => qqs.quiz_question_id === activeQuestionId),
    [sectionSubmissions, activeQuestionId],
  );

  // If completed all the questions in the section, redirect to the last question submission
  useEffect(() => {
    if (!activeQuestionId && sectionSubmissions.length && remainingQuestions === 0) {
      setSearchParams({ qid: sectionSubmissions[sectionSubmissions.length - 1].quiz_question_id });
    }
  }, [activeQuestionId, sectionSubmissions, remainingQuestions, setSearchParams]);

  useEffect(() => {
    setFetchedQuestionIds(q =>
      [...q, fetchNewQuestionStatus?.id].filter(
        (qid, idx, arr) =>
          qid && arr.indexOf(qid) === idx && !sectionSubmissions.some(qqs => qqs.quiz_question_id === qid),
      ),
    );
    setNewQuestionId();
  }, [fetchNewQuestionStatus?.id, sectionSubmissions]);

  // SUBMISSION
  const { performRequest: performSaveSubmission, requestStatus: saveSubmissionStatus } = useStandardApiRequest({
    actionCreator: savePracticeModeQuizQuestionSubmission,
    errorMessage: true,
    onSuccess: () => setSearchParams({ qid: activeQuestionId }),
  });
  const saveSubmission = useCallback(
    (submission, params) => {
      performSaveSubmission(onlineCourse.id, sectionId, activeQuestionId, submission, params);
    },
    [activeQuestionId, onlineCourse.id, performSaveSubmission, sectionId],
  );

  const { performRequest: performResetSubmission, requestStatus: resetSubmissionStatus } = useStandardApiRequest({
    actionCreator: resetQuizQuestionSubmission,
    errorMessage: true,
  });
  const resetSubmission = useCallback(() => {
    performResetSubmission(activeSubmission.id);
  }, [performResetSubmission, activeSubmission]);

  // Token Purchase
  const canTokenPurchase = currentUser.token_balance >= 1; // TOKEN_PRICE;

  const purchaseWithToken = useCallback(() => {
    onTokenPurchase(activeQuestion?.id, sectionId);
  }, [onTokenPurchase, activeQuestion?.id, sectionId]);

  useEffect(() => onSetActiveQuestionId(activeQuestion?.id), [activeQuestion?.id, onSetActiveQuestionId]);
  useEffect(() => () => onSetActiveQuestionId(), [onSetActiveQuestionId]);

  return (
    <PracticeModeSection
      activeQuestion={activeQuestion}
      activeSubmission={activeSubmission}
      canCheckSolution={
        (!currentUser.guest && canView) ||
        tokenPurchases.some(tp => tp.item_type === 'QuizQuestion' && tp.item_id === activeQuestion?.id)
      }
      canFetchQuestion={
        !fetchingSubmissions && remainingQuestions > 1 && fetchNewQuestionStatus && !fetchNewQuestionStatus.fetching
      }
      canTokenPurchase={canTokenPurchase}
      courseSubscription={courseSubscription}
      fetchingSubmissions={fetchingSubmissions}
      fetchNewQuestionStatus={fetchNewQuestionStatus}
      isNewQuestion={!questionIdParam || !sectionSubmissions.some(qqs => qqs.quiz_question_id === questionIdParam)}
      nextAndPrev={nextAndPrev}
      onFetchNewQuestion={fetchNewQuestion}
      onlineCourse={onlineCourse}
      onlineCourseUser={onlineCourseUser}
      onPurchase={onPurchase}
      onResetSubmission={resetSubmission}
      onSaveSubmission={saveSubmission}
      onTokenPurchase={purchaseWithToken}
      remainingQuestions={remainingQuestions}
      resetSubmissionStatus={resetSubmissionStatus}
      savingSubmission={!!saveSubmissionStatus?.fetching}
      sectionId={sectionId}
      submissions={sectionSubmissions}
      subscription={subscription}
      tokenPurchaseStatus={tokenPurchaseStatus}
      totalQuestions={totalQuestions}
      userCourse={userCourse}
      // onStartTrial={onStartTrial}
      // subscriptionTrialStatus={subscriptionTrialStatus}
    />
  );
}

const getQuestion = memoize((id, questions, users, onlineCourse, section, learnModePath) => {
  const qq = questions[id];
  return (
    qq && {
      ...qq,
      user: users[qq.user_id],
      linked_oc_sections: [
        {
          id: section.data.id,
          online_course_id: onlineCourse.id,
          course_code: onlineCourse.course_code,
          chapter_title: findAncestor(section, n => n.type === 'chapter')?.data?.title,
          title: section.data.title,
          path: learnModePath,
        },
      ],
    }
  );
});

PracticeModeSectionCtnr.propTypes = {
  onlineCourse: PropTypes.object.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  sectionId: PropTypes.number.isRequired,
  sectionSubmissions: PropTypes.array.isRequired,
};

export default PracticeModeSectionCtnr;
